const GRADIENT_PAIRS: ReadonlyArray<readonly [string, string]> = [
  ['#8054FF', '#B8A0FF'],
  ['#7FD8B5', '#8054FF'],
  ['#FFCB6B', '#FF8B73'],
  ['#FF8B73', '#8054FF'],
  ['#FF6B9D', '#B8A0FF'],
  ['#B8A0FF', '#7FD8B5'],
  ['#FFCB6B', '#7FD8B5'],
]

/**
 * Reduces a string to a single non-negative integer by hashing each
 * char code. Stable for the same string. Used to pick a deterministic
 * gradient for an avatar so it stays the same per renter across loads.
 *
 * @param {string} input - the string to hash
 * @returns {number} a non-negative integer hash
 */
const hashString = (input: string): number => {
  let hash = 0

  for (let index = 0; index < input.length; index += 1) {
    hash = (hash * 31 + input.charCodeAt(index)) | 0
  }

  return Math.abs(hash)
}

/**
 * Picks one of the curated gradient colour pairs for a given seed.
 *
 * @param {string} seed - the string to seed the pick (typically user id)
 * @returns {readonly [string, string]} the gradient stops [from, to]
 */
const pickGradientFor = (seed: string): readonly [string, string] => {
  if (seed === '') {
    return GRADIENT_PAIRS[0]
  }

  const index = hashString(seed) % GRADIENT_PAIRS.length
  return GRADIENT_PAIRS[index]
}

/**
 * Reduces a renter's display name + last name into 1–2 character
 * initials. Falls back to "QP" when no name data is available so the
 * avatar always renders something legible.
 *
 * @param {string} firstName - the renter's first name
 * @param {string} lastName - the renter's last name
 * @returns {string} the 1–2 character initials
 */
const buildInitials = (firstName: string, lastName: string): string => {
  const first = firstName.trim()
  const last = lastName.trim()

  if (first === '' && last === '') {
    return 'QP'
  }

  if (first === '') {
    return last.charAt(0).toUpperCase()
  }

  if (last === '') {
    return first.charAt(0).toUpperCase()
  }

  return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase()
}

export { buildInitials, pickGradientFor }
